import {
    Box,
    Button,
    Dialog,
    IconButton,
    Skeleton,
    Theme,
    Typography
} from "@mui/material";
import { DialogCloseIcon } from "../../../../assets/SvgIcons";
import { checkImageExists } from "../../../../utils/utilityFunctions";
import {
    Form,
    Formik
} from "formik";
import * as Yup from 'yup';
import { MFTextField } from "../../../../lib/formik";
import {
    leadingZeroDigitRegex,
    numberRegex
} from "../../../../utils/regex";
import LoadingButton from '@mui/lab/LoadingButton';
import {
    useEffect,
    useState
} from "react";
import { getIndividualFundDetails } from "../../../../redux-store/actions/investorProfile";
import { GetIndividualFundDetailsResponseBody } from "../../../../redux-store/types/investorProfile";
import { useDispatch } from "react-redux";

interface PauseSipFormDialogProps {
    open: boolean;
    onClose: () => void;
    handleSubmit: ({ noOfInstallments }: { noOfInstallments: string }) => void;
    selectedPlanInfo: { [key: string]: any }
}

interface InitialValues {
    noOfInstallments: string
}

function SeparatorCircle() {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Typography
                sx={(theme: Theme) => ({
                    width: "3px",
                    height: "3px",
                    backgroundColor: theme.palette.text.secondary,
                    borderRadius: "50%",
                    display: 'block',
                })}
            />
        </Box>
    )
}

const validationSchema = Yup.object().shape({
    noOfInstallments: Yup.string()
        .required('Required Field')
        .test('is-valid-number', 'No. of Installments should be a valid number', function (value) {
            return numberRegex.test(`${value ?? ""}`);
        })
        .test('minimum-installments', 'No. of Installments should be greater than or equal to 1', function (value) {
            return Number(value ?? "") >= 1;
        })
        .test('no-leading-zeros', 'No. of Installments should be a valid number', function (value) {
            return !leadingZeroDigitRegex.test(value ?? "");
        })
})

const initialValues: InitialValues = {
    noOfInstallments: ""
}

const amcIds = ['BDOTR', 'BPIAMC']

export default function PauseSipFormDialog({
    open,
    onClose,
    handleSubmit,
    selectedPlanInfo
}: PauseSipFormDialogProps): JSX.Element {
    const dispatch = useDispatch()

    const [planDetails, setPlanDetails] = useState<{
        amcId: string;
        planName: string;
        fundCategory: string;
        fundTypeCategory: string;
        riskType: string;
    }>({
        amcId: "",
        planName: "",
        fundCategory: "",
        fundTypeCategory: "",
        riskType: ""
    })
    const [isPlanDetailsLoading, setPlanDetailsLoading] = useState<boolean>(false)

    // const fetchPlanDetails = async () => {
    //     try {
    //         setPlanDetailsLoading(true)

    //         const response = await (dispatch(getIndividualFundDetails({
    //             schemeId: selectedPlanInfo?.scheme,
    //         })) as unknown as Promise<GetIndividualFundDetailsResponseBody>);

    //         setPlanDetails({
    //             amcId: response?.amcId ?? "",
    //             planName: response?.name ?? "",
    //             fundCategory: response?.fundCategory ?? "",
    //             fundTypeCategory: response?.fundTypeCategory ?? "",
    //             riskType: response?.riskType ?? ""
    //         })
    //     } catch (error) {
    //         console.log((error as Error)?.message);
    //     } finally {
    //         setPlanDetailsLoading(false)
    //     }
    // }

    // useEffect(() => {
    //     fetchPlanDetails()
    // }, [])

    const checkAmcId = amcIds.includes(selectedPlanInfo?.amcId ?? "") ? (selectedPlanInfo?.amcId ?? "")?.toLowerCase() : (selectedPlanInfo?.amcId ?? "");

    return (
        <Dialog
            aria-labelledby="pause-sip-form-dialog"
            open={open}
            PaperProps={{
                sx: (theme: Theme) => ({
                    maxWidth: '950px',
                    width: "100%",
                    borderRadius: "12px",
                    backgroundColor: theme.palette.common.white
                })
            }}
        >
            <Box>
                <Box
                    sx={(theme: Theme) => ({
                        padding: "25px 30px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: `1px solid ${theme.palette.text.secondary}B2`
                    })}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Pause SIP
                    </Typography>
                    <IconButton
                        aria-label="close-pause-sip-form-dialog"
                        onClick={onClose}
                    >
                        <DialogCloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ padding: "35px 30px 25px 30px" }}
                >
                    {isPlanDetailsLoading && (
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "15px",
                                alignItems: "flex-start"
                            }}
                        >
                            <Skeleton
                                component="div"
                                variant="rounded"
                                sx={{
                                    borderRadius: "50%",
                                    width: { xs: "40px", md: "45px", lg: "50px" },
                                    height: { xs: "40px", md: "45px", lg: "50px" },
                                }}
                            />
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    rowGap: "7px"
                                }}
                            >
                                <Skeleton
                                    component="div"
                                    sx={(theme: Theme) => ({
                                        ...theme.typography.body_600,
                                        width: "80%"
                                    })}
                                />
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        columnGap: "8px"
                                    }}
                                >
                                    <Skeleton
                                        component="div"
                                        sx={(theme: Theme) => ({
                                            ...theme.typography.body_500,
                                            width: "100%"
                                        })}
                                    />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Skeleton
                                            component="div"
                                            variant="rounded"
                                            sx={{
                                                borderRadius: "50%",
                                                width: "3px",
                                                height: "3px",
                                            }}
                                        />
                                    </Box>
                                    <Skeleton
                                        component="div"
                                        sx={(theme: Theme) => ({
                                            ...theme.typography.body_500,
                                            width: "100%"
                                        })}
                                    />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Skeleton
                                            component="div"
                                            variant="rounded"
                                            sx={{
                                                borderRadius: "50%",
                                                width: "3px",
                                                height: "3px",
                                            }}
                                        />
                                    </Box>
                                    <Skeleton
                                        component="div"
                                        sx={(theme: Theme) => ({
                                            ...theme.typography.body_500,
                                            width: "100%"
                                        })}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    )}
                    {!isPlanDetailsLoading && (
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "15px",
                                // alignItems: "flex-start"
                                alignItems: 'center'
                            }}
                        >
                            <Box
                                sx={{
                                    borderRadius: "50%",
                                    overflow: "hidden",
                                    width: "50px",
                                    height: "50px",
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    component='img'
                                    src={checkImageExists(`${checkAmcId}`) ? `/images/amcIcons/${checkAmcId}.svg` : `/images/amcIcons/default.svg`}
                                    alt={selectedPlanInfo?.schemeName ?? ""}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        flexShrink: 0,
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    rowGap: "7px"
                                }}
                            >
                                <Typography
                                    variant="body_600"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "22.5px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {selectedPlanInfo?.schemeName ?? ""}
                                </Typography>
                                {/* <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        columnGap: "8px"
                                    }}
                                >
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.secondary
                                        })}
                                    >
                                        {planDetails?.fundCategory ?? ""}
                                    </Typography>
                                    {(planDetails?.fundTypeCategory ?? "") && (
                                        <>
                                            <SeparatorCircle />
                                            <Typography
                                                variant="body_500"
                                                sx={(theme: Theme) => ({
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.02px",
                                                    color: theme.palette.text.secondary
                                                })}
                                            >
                                                {planDetails?.fundTypeCategory ?? ""}
                                            </Typography>
                                        </>
                                    )}
                                    {(planDetails?.riskType ?? "") && (
                                        <>
                                            <SeparatorCircle />
                                            <Typography
                                                variant="body_500"
                                                sx={(theme: Theme) => ({
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.02px",
                                                    color: theme.palette.text.secondary
                                                })}
                                            >
                                                {planDetails?.riskType ?? ""}
                                            </Typography>
                                        </>
                                    )}
                                </Box> */}
                            </Box>
                        </Box>
                    )}
                    <Formik
                        validationSchema={validationSchema}
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                    >
                        <Form>
                            <Box
                                sx={(theme: Theme) => ({
                                    marginTop: "30px",
                                    paddingBottom: "10px",
                                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`
                                })}
                            >
                                <Box sx={{ width: "50%" }}>
                                    <MFTextField
                                        autoFocus
                                        name="noOfInstallments"
                                        label="No. of Installments"
                                        placeholder="Enter No. of Installments"
                                        disabled={isPlanDetailsLoading}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset>legend": {
                                                    fontSize: { xs: "0.6em", md: "0.7em", lg: "0.78em" }
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ marginTop: "25px" }}>
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        fontWeight: 600,
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.primary[500]
                                    })}
                                >
                                    Note:
                                    <Typography
                                        component="span"
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            fontWeight: 400,
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.secondary,
                                            marginLeft: "8px"
                                        })}
                                    >
                                        SIP installments will resume automatically once the pause period is over
                                    </Typography>
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    marginTop: "25px",
                                    columnGap: "25px",
                                }}
                            >
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={onClose}
                                    sx={(theme: Theme) => ({
                                        ...theme.typography.body_500,
                                        fontWeight: 600,
                                        lineHeight: "20px",
                                        color: theme.palette.text.primary,
                                        border: `1px solid ${theme.palette.customColors.pastelGrey}`,
                                        borderRadius: "10px",
                                        padding: "13px 56px",
                                        "&:hover": {
                                            border: `1px solid ${theme.palette.grey[100]}`,
                                            background: theme.palette.grey[50]
                                        }
                                    })}
                                    disabled={isPlanDetailsLoading}
                                >
                                    Cancel
                                </Button>
                                <LoadingButton
                                    fullWidth
                                    loadingPosition="start"
                                    type="submit"
                                    variant="contained"
                                    disabled={isPlanDetailsLoading}
                                    sx={(theme: Theme) => ({
                                        ...theme.typography.body_500,
                                        fontWeight: 700,
                                        lineHeight: "22.5px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.textDark.primary,
                                        borderRadius: "10px",
                                        padding: "13px 56px",
                                        boxShadow: "none",
                                        backgroundColor: theme.palette.primary[500],
                                    })}
                                >
                                    Submit
                                </LoadingButton>
                            </Box>
                        </Form>
                    </Formik>
                </Box>
            </Box>
        </Dialog >
    )
} 